import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import Box from '@zenbusiness/zui/Box';
import Heading from '@zenbusiness/zui/Heading';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

import FormStepModal from '#client/components/FormStepModal';
import { ActionButtonsGrid } from '#client/components/Grid';
import Skeleton from '#client/components/Skeleton';
import useModalContext from '#client/hooks/useModalContext';
import useModalDisplayTracker from '#client/hooks/useModalDisplayTracker';

import { DialogContentStyled, Wrapper } from './AstraVGSFormModal.styles';
import CardAddress from './components/CardAddress';
import CardAddressTitle from './components/CardAddressTitle';
import CardDateCVV from './components/CardDateCVV';
import VGSFieldWithMessage from './components/VGSFieldWithMessage';

import useAstraCardSubmit from './hooks/useAstraCardSubmit';
import useInitVgs from './hooks/useInitVgs';
import { checkShowErrors, isAllFieldsValid } from './utils/field';

const REQUIRED_MESSAGE = 'Please fill out all required fields';

export default function AstraVGSFormModal({ handleClose, isClearAllModal = false }) {
  const { form, formState, street, city, state, postalCode, fullName, loading } =
    useInitVgs(handleClose);
  const { clearAllModal } = useModalContext();

  const { handleSubmit, isSubmitting } = useAstraCardSubmit(handleClose, form);
  const handleCloseModal = () => {
    if (isClearAllModal) {
      clearAllModal();
    }

    handleClose();
  };

  const showErrors = checkShowErrors(formState);

  const buttonPressed = useButtonPressedCallback({
    elementName: 'submit_new_dc_astra',
    elementText: 'Submit',
    appPageName: 'add_new_dc_astra_modal'
  });

  const handleSubmitAstraCard = () => {
    buttonPressed();
    handleSubmit();
  };

  useModalDisplayTracker({
    elementName: 'add_new_dc_astra_modal',
    elementText: 'add_new_dc_astra_modal',
    appPageName: 'active_banking_dashboard'
  });

  return (
    <FormStepModal
      handleBack={handleCloseModal}
      handleClose={handleCloseModal}
      title="Instant deposit"
    >
      <DialogContentStyled>
        <Wrapper>
          <Stack
            gap={{ xs: '24px', md: '32px' }}
            mb={{ xs: '16px', md: '24px' }}
          >
            <Heading
              size="3xl"
              weight="bold"
            >
              Add your debit card
            </Heading>
            <Text
              color="primary"
              size="lg"
            >
              Must be Visa® or Mastercard® and issued to{' '}
              {loading ? <Skeleton width={200} /> : fullName}
            </Text>
          </Stack>
          <Stack gap="16px">
            <Box
              display="flex"
              flexDirection={{ xs: 'column', md: 'row' }}
              gap="16px"
            >
              <VGSFieldWithMessage
                id="cc-first-name"
                isError={showErrors.first_name}
                message={REQUIRED_MESSAGE}
              />

              <VGSFieldWithMessage
                id="cc-last-name"
                isError={showErrors.last_name}
                message={REQUIRED_MESSAGE}
              />
            </Box>

            <CardDateCVV formState={formState} />

            <CardAddressTitle />
            <CardAddress
              address={{
                street,
                city,
                state,
                postalCode
              }}
              loading={loading}
            >
              <VGSFieldWithMessage
                id="street_line_1"
                isError={showErrors.street_line_1}
                message={REQUIRED_MESSAGE}
              />

              <VGSFieldWithMessage
                id="city"
                isError={showErrors.city}
                message={REQUIRED_MESSAGE}
              />

              <VGSFieldWithMessage
                id="state"
                isError={showErrors.state}
                message={REQUIRED_MESSAGE}
              />

              <VGSFieldWithMessage
                id="zip_code"
                isError={showErrors.zip_code}
                message={REQUIRED_MESSAGE}
              />
            </CardAddress>
            <Box display="none">
              <VGSFieldWithMessage
                id="added_by_user"
                isError={showErrors.added_by_user}
                message={REQUIRED_MESSAGE}
              />
            </Box>
          </Stack>
        </Wrapper>
      </DialogContentStyled>
      <ActionButtonsGrid>
        <LoadingButton
          color="primary"
          disabled={!isAllFieldsValid(formState)}
          fullWidth
          loading={isSubmitting}
          onClick={handleSubmitAstraCard}
          size="large"
          variant="contained"
        >
          Submit
        </LoadingButton>
      </ActionButtonsGrid>
    </FormStepModal>
  );
}

AstraVGSFormModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  isClearAllModal: PropTypes.bool
};
